import { Drawer, makeStyles } from '@material-ui/core';
import { CircularProgress, Typography } from '@mui/material';
import { WorkflowList } from "./workflowList";
import { WorkflowToolbar } from "./workflowToolbar";
import { WorkflowSearchCriteria } from "model/WorkflowSearchCriteria";
import { getWorkflows } from "services/workflowApiCaller";
import { Workflow } from "model/Workflow";
import "style/workflow.scss";
import { useEffect, useState, useRef, useCallback } from 'react';

interface WorkflowDrawerProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles((theme?: any) => ({
    paper: {
      background: 'lightgrey',
      color: 'white',
      width: '500px'
    }
  }));

export function WorkflowDrawer(props: WorkflowDrawerProps) {
    const styles = useStyles();
    const [isLoaded, setIsLoaded] = useState(false);
    const [searchCriteria, setSearchCriteria] = useState(new WorkflowSearchCriteria());
    const [workflows, setWorkflows] = useState([] as Workflow[]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    
  useEffect(() => {
    if(props.open){
      resetAndSearch();
    }
  }, [props.open]);

  useEffect(() => {
    // Debounce pour la recherche textuelle
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (props.open) {
        resetAndSearch();
      }
    }, 500);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchCriteria.searchText, searchCriteria.statusId, searchCriteria.workflowTypeId]);

  function resetAndSearch() {
    const newCriteria = { ...searchCriteria, skip: 0 };
    setSearchCriteria(newCriteria);
    setWorkflows([]);
    setHasMore(true);
    search(newCriteria, true);
  }

  function search(criteria: WorkflowSearchCriteria, isReset = false){
    if (loading) return;
    
    setLoading(true);
    getWorkflows(criteria)
        .then((response) => {
          if (isReset) {
            setWorkflows(response);
          } else {
            setWorkflows(prev => [...prev, ...response]);
          }
          setHasMore(response.length === criteria.take);
          setIsLoaded(true);
          setLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setLoading(false);
        });
  }

  function criteriasChange(propertyName: string, value: any) {
    setSearchCriteria(prev => ({
      ...prev,
      [propertyName]: value
    }));
  }

  function handleDelete(workflowId: string) {
    // Retirer le workflow de la liste
    setWorkflows(prev => prev.filter(w => w.id !== workflowId));
  }

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loading || !hasMore) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    
    // Charger plus quand on est à 80% du scroll
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      const newCriteria = {
        ...searchCriteria,
        skip: workflows.length
      };
      setSearchCriteria(newCriteria);
      search(newCriteria);
    }
  }, [loading, hasMore, workflows.length, searchCriteria]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <>
      <Drawer
        open={props.open}
        onClose={props.onClose}
        anchor="right"
        classes={{ paper: styles.paper }}
      >
        <div className="workflowDrawerContainer">
          <div className="workflowDrawerHeader">
            <Typography variant="h6" sx={{ padding: "10px", color: "black" }}>
              Workflows ({workflows.length}{hasMore ? '+' : ''})
            </Typography>
            <WorkflowToolbar criterias={searchCriteria} onChange={criteriasChange} />
          </div>
          <div className="workflowDrawerBody" ref={scrollContainerRef}>
            <WorkflowList workflows={workflows} onDelete={handleDelete} />
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
              </div>
            )}
            {!hasMore && workflows.length > 0 && (
              <Typography 
                variant="body2" 
                sx={{ textAlign: 'center', padding: '20px', color: 'gray' }}
              >
                Tous les workflows sont chargés
              </Typography>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
}
